import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { connect } from 'react-redux';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { Typography } from '@mui/material';
import BuildingsLoader from '../loaders/BuildingsLoader';
import ModbusTemplatesLoader from '../loaders/ModbusTemplatesLoader';
import { DeploymentsLoader } from '../loaders/deployments';
import DeploymentDetails from './DeploymentDetails';
import { deploymentOnline } from '../../utils/deployments';
import {
  defaultTableOptions,
  defaultTableInitialState,
  extractSortingParams,
  extractFilterParams,
  extractExpandedParams,
  sortingUpdater,
  filterUpdater,
  setExpanded,
  LIST_DELIMITER,
  MISSING_VALUE,
} from '../util/data-table';
import { CellList } from '../util/DataTableCells';

TimeAgo.addDefaultLocale(en);

const DeploymentsPage = ({ deployments, buildingsById, orgsById }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sorting = useMemo(() => extractSortingParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => extractFilterParams(searchParams), [searchParams]);
  const expanded = useMemo(() => extractExpandedParams(searchParams), [searchParams]);
  const onSortingChange = useCallback(
    (updater) => setSearchParams(sortingUpdater(sorting)(updater)),
    [setSearchParams, sorting]
  );
  const onColumnFiltersChange = useCallback(
    (updater) => setSearchParams(filterUpdater(columnFilters)(updater)),
    [setSearchParams, columnFilters]
  );
  const onExpandedChange = useCallback(
    (updater) => setExpanded(updater, expanded, setSearchParams),
    [setSearchParams, expanded]
  );

  const timeAgo = useMemo(() => new TimeAgo('en-US'), []);
  const now = useMemo(() => new Date(), []);
  const observedBuildingNames = useMemo(
    () => Object.values(buildingsById).map(({ name }) => name),
    [buildingsById]
  );
  const columns = useMemo(
    () => [
      // Note: attempting to manage initial hidden columns doesn't work right because this page also loads the data
      { accessorKey: 'o_name', header: 'Organization', filterVariant: 'autocomplete' },
      { accessorKey: 'name', header: 'Deployment Name', filterVariant: 'autocomplete' },
      {
        accessorKey: 'buildings',
        header: 'Buildings',
        accessorFn: ({ buildings }) =>
          buildings?.map((bid) => buildingsById[bid]?.name ?? bid).join(LIST_DELIMITER) ||
          MISSING_VALUE,
        filterVariant: 'multi-select',
        filterSelectOptions: observedBuildingNames,
        Cell: CellList,
      },
      {
        accessorKey: 'last_heartbeat',
        header: 'Last Contact',
        accessorFn: ({ last_heartbeat }) =>
          // eslint-disable-next-line camelcase
          last_heartbeat == null ? null : new Date(last_heartbeat),
        Cell: ({ renderedCellValue }) =>
          renderedCellValue == null ? null : `${timeAgo.format(renderedCellValue)}`,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'checkbox',
        accessorFn: ({ last_heartbeat }) => deploymentOnline(now, last_heartbeat),
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <Typography variant="button">ONLINE</Typography>
          ) : (
            <Typography variant="button" color="error">
              OFFLINE
            </Typography>
          ),
      },
    ],
    [buildingsById, now, timeAgo, observedBuildingNames]
  );

  const columnIds = useMemo(() => columns.map((c) => c.accessorKey), [columns]);
  const data = useMemo(
    () => (deployments ?? []).map((d) => ({ o_name: orgsById[d.org_id]?.name, ...d })),
    [deployments, orgsById]
  );

  const buildingSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const buildingColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: buildingColumnFilters,
        sorting: buildingSorting,
        expanded,
      }),
    },
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    onExpandedChange,
    enableExpandAll: false,
    autoResetPageIndex: false,
    // eslint-disable-next-line no-unused-vars
    renderDetailPanel: ({ row, table }) => (
      <DeploymentDetails deployment={row.original} now={now} />
    ),
    state: {
      sorting: buildingSorting,
      columnFilters: buildingColumnFilters,
      expanded,
      isLoading: !deployments.length,
    },
  });
  return (
    <div>
      <ModbusTemplatesLoader />
      <DeploymentsLoader />
      <BuildingsLoader />
      <MaterialReactTable table={mrt} />
    </div>
  );
};

function mapStateToProps(state) {
  const {
    entities: {
      deployments: { deployments },
      buildings: { byId: buildingsById },
    },
    users: { orgsById },
  } = state;
  return {
    deployments: Object.values(deployments),
    buildingsById,
    orgsById,
  };
}

// eslint-disable-next-line no-unused-vars
function mapDispatchToProps(dispatch) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentsPage);
